import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, syncVendorOpeningBalance } from "@/lib/party-ledger";
import type {
  AddressFormValues,
  ContactFormValues,
  VendorFormValues,
} from "@/features/vendors/schema";
import type {
  Vendor,
  VendorAddress,
  VendorApMovement,
  VendorContact,
  VendorListRow,
  VendorNote,
} from "@/features/vendors/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateVendor(raw: Vendor): Vendor {
  return {
    ...raw,
    credit_limit: money(raw.credit_limit),
    credit_days: money(raw.credit_days),
    opening_balance: money(raw.opening_balance),
    outstanding_balance: money(raw.outstanding_balance),
  };
}

export function vendorToForm(v: Vendor): VendorFormValues {
  return {
    name: v.name,
    code: v.code,
    vendor_type: v.vendor_type,
    gst_treatment: v.gst_treatment,
    gstin: v.gstin ?? "",
    pan_no: v.pan_no ?? "",
    work_phone: v.work_phone ?? "",
    email: v.email ?? "",
    website: v.website ?? "",
    addr1: v.addr1,
    addr2: v.addr2 ?? "",
    city: v.city,
    state: v.state,
    pincode: v.pincode,
    place_of_supply: v.place_of_supply ?? "",
    payment_terms: v.payment_terms,
    credit_days: v.credit_days,
    credit_limit: v.credit_limit,
    batch_prefix: v.batch_prefix,
    notes: v.notes ?? "",
    opening_balance: v.opening_balance,
    opening_balance_as_of: v.opening_balance_as_of ?? "",
    is_active: v.is_active,
  };
}

function normalizeVendor(values: VendorFormValues) {
  return {
    name: values.name.trim(),
    code: values.code.trim().toUpperCase(),
    vendor_type: values.vendor_type,
    gst_treatment: values.gst_treatment,
    gstin: values.gstin?.trim().toUpperCase() || null,
    pan_no: values.pan_no?.trim().toUpperCase() || null,
    work_phone: text(values.work_phone),
    email: text(values.email),
    website: text(values.website),
    addr1: values.addr1.trim(),
    addr2: text(values.addr2),
    city: values.city.trim(),
    state: values.state.trim(),
    pincode: values.pincode.trim(),
    place_of_supply: text(values.place_of_supply) ?? values.state.trim(),
    payment_terms: values.payment_terms,
    credit_days: values.credit_days,
    credit_limit: values.credit_limit,
    batch_prefix: values.batch_prefix.trim().toUpperCase(),
    notes: text(values.notes),
    opening_balance: values.opening_balance,
    opening_balance_as_of: text(values.opening_balance_as_of),
    is_active: values.is_active,
  };
}

export function useVendors() {
  return useQuery({
    queryKey: ["vendors"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<VendorListRow[]> => {
      const { data, error } = await supabase.from("vendors").select("*").order("name");
      if (error) throw error;
      const vendors = (data ?? []).map((v) => hydrateVendor(v as Vendor));
      const ids = vendors.map((v) => v.id);
      if (!ids.length) return vendors;

      const { data: contacts, error: cErr } = await supabase
        .from("vendor_contacts")
        .select("vendor_id, name, email, mobile, is_primary")
        .in("vendor_id", ids)
        .eq("is_primary", true);
      if (cErr) throw cErr;

      const byVendor = new Map(
        (contacts ?? []).map((c) => [
          c.vendor_id as string,
          { name: c.name as string, email: c.email as string | null, mobile: c.mobile as string | null },
        ]),
      );

      return vendors.map((v) => ({
        ...v,
        primary_contact: byVendor.get(v.id) ?? null,
      }));
    },
  });
}

export function useVendor(id?: string) {
  return useQuery({
    queryKey: ["vendors", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<Vendor> => {
      const { data, error } = await supabase.from("vendors").select("*").eq("id", id!).single();
      if (error) throw error;
      return hydrateVendor(data as Vendor);
    },
  });
}

export function useVendorCounts(vendorId?: string) {
  return useQuery({
    queryKey: ["vendors", vendorId, "counts"],
    enabled: isSupabaseConfigured && Boolean(vendorId),
    queryFn: async () => {
      const [contacts, addresses, notes] = await Promise.all([
        supabase
          .from("vendor_contacts")
          .select("id", { count: "exact", head: true })
          .eq("vendor_id", vendorId!),
        supabase
          .from("vendor_addresses")
          .select("id", { count: "exact", head: true })
          .eq("vendor_id", vendorId!),
        supabase
          .from("vendor_notes")
          .select("id", { count: "exact", head: true })
          .eq("vendor_id", vendorId!),
      ]);
      if (contacts.error) throw contacts.error;
      if (addresses.error) throw addresses.error;
      if (notes.error) throw notes.error;
      return {
        contacts: contacts.count ?? 0,
        addresses: addresses.count ?? 0,
        notes: notes.count ?? 0,
      };
    },
  });
}

export function useNextVendorCode(enabled = true) {
  return useQuery({
    queryKey: ["vendors", "next-code"],
    enabled: isSupabaseConfigured && enabled,
    queryFn: async () => {
      const { data: company } = await supabase.from("company").select("vendor_prefix").limit(1).maybeSingle();
      const prefix = (company?.vendor_prefix as string | undefined)?.trim().toUpperCase() || "VN";
      const { data, error } = await supabase
        .from("vendors")
        .select("code")
        .ilike("code", `${prefix}-%`)
        .order("code", { ascending: false })
        .limit(1);
      if (error) throw error;
      const last = data?.[0]?.code as string | undefined;
      const n = last ? Number.parseInt(last.replace(`${prefix}-`, ""), 10) : 0;
      return `${prefix}-${String((Number.isNaN(n) ? 0 : n) + 1).padStart(4, "0")}`;
    },
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: VendorFormValues) => {
      const payload = {
        ...normalizeVendor(values),
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      };
      const { data, error } = await supabase.from("vendors").insert(payload).select("*").single();
      if (error) throw error;
      const vendor = hydrateVendor(data as Vendor);
      await syncVendorOpeningBalance(
        vendor.id,
        values.opening_balance,
        text(values.opening_balance_as_of),
      );
      return vendor;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      toast.success("Vendor created");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create vendor"),
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: VendorFormValues }) => {
      const payload = {
        ...normalizeVendor(values),
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = await supabase.from("vendors").update(payload).eq("id", id).select("*").single();
      if (error) throw error;
      await syncVendorOpeningBalance(id, values.opening_balance, text(values.opening_balance_as_of));
      return hydrateVendor(data as Vendor);
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      toast.success("Vendor updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update vendor"),
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vendors").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Vendor deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete vendor"),
  });
}

export function useBulkSetVendorActive() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ ids, is_active }: { ids: string[]; is_active: boolean }) => {
      const { error } = await supabase
        .from("vendors")
        .update({ is_active, updated_by: user?.id ?? null, updated_at: new Date().toISOString() })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Vendors updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update vendors"),
  });
}

export function useVendorContacts(vendorId?: string) {
  return useQuery({
    queryKey: ["vendor_contacts", vendorId],
    enabled: isSupabaseConfigured && Boolean(vendorId),
    queryFn: async (): Promise<VendorContact[]> => {
      const { data, error } = await supabase
        .from("vendor_contacts")
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("is_primary", { ascending: false })
        .order("name");
      if (error) throw error;
      return (data ?? []).map((c) => ({ ...(c as VendorContact), tags: (c as VendorContact).tags ?? [] }));
    },
  });
}

async function clearPrimaryContacts(vendorId: string, exceptId?: string) {
  let q = supabase.from("vendor_contacts").update({ is_primary: false }).eq("vendor_id", vendorId).eq("is_primary", true);
  if (exceptId) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw error;
}

export function useUpsertVendorContact(vendorId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: ContactFormValues }) => {
      if (values.is_primary) await clearPrimaryContacts(vendorId, id);
      const payload = {
        vendor_id: vendorId,
        name: values.name.trim(),
        designation: text(values.designation),
        department: text(values.department),
        mobile: text(values.mobile),
        alt_mobile: text(values.alt_mobile),
        email: text(values.email),
        is_primary: values.is_primary,
        is_active: values.is_active,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase.from("vendor_contacts").update(payload).eq("id", id).select("*").single();
        if (error) throw error;
        return data as VendorContact;
      }
      const { data, error } = await supabase
        .from("vendor_contacts")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as VendorContact;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_contacts", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Contact saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save contact"),
  });
}

export function useDeleteVendorContact(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vendor_contacts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_contacts", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      toast.success("Contact deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete contact"),
  });
}

export function useVendorAddresses(vendorId?: string) {
  return useQuery({
    queryKey: ["vendor_addresses", vendorId],
    enabled: isSupabaseConfigured && Boolean(vendorId),
    queryFn: async (): Promise<VendorAddress[]> => {
      const { data, error } = await supabase
        .from("vendor_addresses")
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("is_default", { ascending: false })
        .order("title");
      if (error) throw error;
      return (data ?? []) as VendorAddress[];
    },
  });
}

export function useUpsertVendorAddress(vendorId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: AddressFormValues }) => {
      if (values.is_default) {
        await supabase
          .from("vendor_addresses")
          .update({ is_default: false })
          .eq("vendor_id", vendorId)
          .eq("is_default", true);
      }
      const payload = {
        vendor_id: vendorId,
        title: values.title.trim(),
        addr1: values.addr1.trim(),
        addr2: text(values.addr2),
        city: values.city.trim(),
        state: values.state.trim(),
        pincode: values.pincode.trim(),
        address_type: values.address_type,
        is_default: values.is_default,
        notes: text(values.notes),
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (id) {
        const { data, error } = await supabase.from("vendor_addresses").update(payload).eq("id", id).select("*").single();
        if (error) throw error;
        return data as VendorAddress;
      }
      const { data, error } = await supabase
        .from("vendor_addresses")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as VendorAddress;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_addresses", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      toast.success("Address saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save address"),
  });
}

export function useDeleteVendorAddress(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vendor_addresses").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_addresses", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      toast.success("Address deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete address"),
  });
}

export function useVendorNotes(vendorId?: string) {
  return useQuery({
    queryKey: ["vendor_notes", vendorId],
    enabled: isSupabaseConfigured && Boolean(vendorId),
    queryFn: async (): Promise<VendorNote[]> => {
      const { data, error } = await supabase
        .from("vendor_notes")
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as VendorNote[];
    },
  });
}

export function useCreateVendorNote(vendorId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (body: string) => {
      const { data, error } = await supabase
        .from("vendor_notes")
        .insert({ vendor_id: vendorId, body: body.trim(), created_by: user?.id ?? null })
        .select("*")
        .single();
      if (error) throw error;
      return data as VendorNote;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_notes", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      toast.success("Note added");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to add note"),
  });
}

export function useDeleteVendorNote(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vendor_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_notes", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId, "counts"] });
      toast.success("Note deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete note"),
  });
}

export function useVendorLedger(vendorId?: string, range?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ["vendor_ap_movements", vendorId, range?.from, range?.to],
    enabled: isSupabaseConfigured && Boolean(vendorId),
    queryFn: async (): Promise<VendorApMovement[]> => {
      let q = supabase
        .from("vendor_ap_movements")
        .select("*")
        .eq("vendor_id", vendorId!)
        .order("movement_date", { ascending: true })
        .order("created_at", { ascending: true });
      if (range?.from) q = q.gte("movement_date", range.from);
      if (range?.to) q = q.lte("movement_date", range.to);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...(row as VendorApMovement),
        amount: money((row as VendorApMovement).amount),
        source_type: (row as VendorApMovement).source_type ?? null,
        source_id: (row as VendorApMovement).source_id ?? null,
      }));
    },
  });
}

export function usePostVendorAdjustment(vendorId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: {
      amount: number;
      movementDate: string;
      reference?: string;
      notes?: string;
    }) => {
      return postPartyMovement({
        party: "vendor",
        partyId: vendorId,
        kind: "adjustment",
        amount: values.amount,
        movementDate: values.movementDate,
        reference: values.reference ?? "Adjustment",
        notes: values.notes,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors", vendorId] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Adjustment posted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to post adjustment"),
  });
}
